/**
 * Utilitário de Toast - Wrapper para Sonner com API simplificada
 * 
 * Este módulo fornece uma API unificada para exibir notificações toast
 * com tipos semânticos (success, error, warning, info) e opções adicionais.
 * 
 * Uso básico:
 * import { showToast } from "@/lib/toast";
 * 
 * showToast.success("Salvo com sucesso!");
 * showToast.error("Erro ao processar", "Verifique os dados e tente novamente");
 * showToast.warning("Ação irreversível");
 * showToast.info("Dica: você pode arrastar itens");
 * 
 * Com opções:
 * showToast.success("Arquivo enviado", undefined, {
 *   duration: 6000,
 *   action: { label: "Desfazer", onClick: () => handleUndo() }
 * });
 */

import { toast } from "sonner";

// Tipos de opções adicionais para o toast
interface ToastOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
  onAutoClose?: () => void;
}

/**
 * Exibe toast de sucesso (verde)
 */
const success = (title: string, description?: string, options?: ToastOptions) => {
  return toast.success(title, {
    description,
    ...options,
  });
};

/**
 * Exibe toast de erro (vermelho)
 */
const error = (title: string, description?: string, options?: ToastOptions) => {
  return toast.error(title, {
    description,
    duration: options?.duration ?? 6000, // Erros ficam mais tempo visíveis
    ...options,
  });
};

/**
 * Exibe toast de aviso (amarelo/âmbar)
 */
const warning = (title: string, description?: string, options?: ToastOptions) => {
  return toast.warning(title, {
    description,
    ...options,
  });
};

/**
 * Exibe toast informativo (azul)
 */
const info = (title: string, description?: string, options?: ToastOptions) => {
  return toast.info(title, {
    description,
    ...options,
  });
};

/**
 * Exibe toast de loading com promise
 * Automaticamente muda para success/error baseado no resultado
 */
const promise = <T>(
  promiseFn: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: unknown) => string);
  }
) => {
  return toast.promise(promiseFn, messages);
};

/**
 * Fecha um toast específico por ID ou todos se não passar ID
 */
const dismiss = (toastId?: string | number) => {
  toast.dismiss(toastId);
};

/**
 * API unificada de toast
 */
export const showToast = {
  success,
  error,
  warning,
  info,
  promise,
  dismiss,
  // Acesso direto ao toast original do Sonner para casos avançados
  raw: toast,
};

// Re-exporta o toast original para migração gradual
export { toast } from "sonner";
